import { z } from 'zod';
import { teamSchema } from '../team.js';
export const MAX_CONSOLIDATION_CANDIDATES = 5;
// Both outcomes are provisional: nothing is written to a partner until approval, so either can be taken back.
export const consolidationDecisionSchema = z.enum(['AwaitingDecision', 'MatchedPartner', 'NewPartner']);
export const getConsolidationDecision = ({ partnerId, createsNewPartner, }) => {
    if (createsNewPartner) {
        return 'NewPartner';
    }
    return partnerId !== null ? 'MatchedPartner' : 'AwaitingDecision';
};
export const isValidationConsolidationOutstanding = (ticket) => getConsolidationDecision(ticket) === 'AwaitingDecision';
export const consolidationProfileSourceSchema = z.enum(['Partner', 'OnboardingApplication']);
// The onboarding form captures one address line, so `nr` is only ever set on the partner side.
export const consolidationAddressSchema = z.object({
    street: z.string().nullable(),
    nr: z.string().nullable(),
    zip: z.string().nullable(),
    city: z.string().nullable(),
});
export const consolidationContactSchema = z.object({
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    role: z.string().nullable(),
});
export const consolidationBillingAddressSchema = consolidationAddressSchema.extend({
    companyName: z.string().nullable(),
    email: z.string().nullable(),
});
// The same shape read off either side, so the two can be shown against each other field by field.
export const consolidationProfileSchema = z.object({
    source: consolidationProfileSourceSchema,
    partnerId: z.number().nullable(),
    companyName: z.string().nullable(),
    team: teamSchema.nullable(),
    contact: consolidationContactSchema.nullable(),
    numberOfEmployees: z.string().nullable(),
    address: consolidationAddressSchema.nullable(),
    website: z.string().nullable(),
    billingAddress: consolidationBillingAddressSchema.nullable(),
});
// `matchedBy` and `score` are not rendered; they are carried so the weighting can be tuned against real data.
export const consolidationCandidateSchema = z.object({
    partnerId: z.number(),
    partnerUuid: z.uuid(),
    name: z.string(),
    score: z.number(),
    matchedBy: z.array(z.string()),
    profile: consolidationProfileSchema,
});
export const validationConsolidationSchema = z.object({
    decision: consolidationDecisionSchema,
    partner: consolidationProfileSchema.nullable(),
    // Absent for a partner migrated off the old contract model, who never filled in the onboarding form.
    application: consolidationProfileSchema.nullable(),
});
// Awaiting a decision is a state, not something anyone can ask for; undoing is what returns you to it.
export const consolidationTargetSchema = consolidationDecisionSchema.exclude(['AwaitingDecision']);
// A discriminated union would model this better, but `createZodDto` cannot extend one. The refine is the guard.
export const recordConsolidationDecisionSchema = z
    .object({
    decision: consolidationTargetSchema,
    partnerId: z.number().int().positive().nullable().default(null),
})
    .refine((input) => (input.decision === 'MatchedPartner') === (input.partnerId !== null), {
    message: 'A partner is required to consolidate with one, and must be absent for a new partner.',
    path: ['partnerId'],
});
